import { Comment, Environment, Metadata, Plan } from '@workspaces/types'
import { nanoid } from 'nanoid'
import CommentsService from '@/services/comments.service'
import BrowserEnvironmentResolver from './environment.helper'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { usePlanStore } from '@/store/plan'
import { getFetchedPlansForComments } from './plans.helper'
import { subscribeToComments as subscribeToCommentsService } from '@workspaces/services'
import {
  getRealtimeReactionForQuickAccessComments,
  isQuickAccessCommentEnabled,
} from '@/plan/metadata/metadata.helper'

export function isAttachmentNameUnique(
  attachments: Comment.Attachment[],
  fileName: string,
): boolean {
  const attachmentsWithSameName = attachments.some(
    (attachment) => attachment.humanName === fileName,
  )
  return attachmentsWithSameName
}

export function getCloudNameForAttachment(
  commentId: string,
  fileName: string,
): string {
  const uuid = nanoid()
  const cloudName = `${commentId}-${fileName}-${uuid}`
  return cloudName
}

export function getCommentCounts(
  comments: Comment.Comment[],
  user: string,
): {
  totalComments: number
  newComments: number
} {
  const totalComments = comments.length
  const newComments = comments.filter((comment) =>
    isNewComment(comment, user),
  ).length

  return { totalComments, newComments }
}

export function isNewComment(comment: Comment.Comment, user: string): boolean {
  if (comment.created_by === user) {
    return comment.newForCreator ?? false
  }
  return comment.new ?? false
}

export function filterCommentsBasedOnOptions(
  comments: Comment.Comment[],
  options?: {
    onlyNew?: boolean
    onlyMyMessages?: { userName: string; filterByMe: boolean }
  },
): Comment.Comment[] {
  if (!options) {
    return comments
  }

  let filteredComments = comments
  if (options.onlyNew !== undefined) {
    if (options.onlyNew) {
      filteredComments = filteredComments.filter(
        (comment) => comment.new && comment.newForCreator,
      )
    } else {
      filteredComments = filteredComments.filter((comment) => !comment.new)
    }
  }

  if (options.onlyMyMessages !== undefined) {
    const filterByMe = options.onlyMyMessages.filterByMe
    const userName = options.onlyMyMessages.userName

    if (!userName || filterByMe === undefined) {
      throw new Error(
        'When filtering comments by user, both userName and filterByMe must be provided',
      )
    }

    let patternForFilteringByUserName = userName
    if (userName.includes('@clearchannel.com')) {
      patternForFilteringByUserName = '@clearchannel.com'
    }

    if (filterByMe) {
      filteredComments = filteredComments.filter((comment) =>
        comment.created_by.includes(patternForFilteringByUserName),
      )
    } else {
      filteredComments = filteredComments.filter(
        (comment) =>
          !comment.created_by.includes(patternForFilteringByUserName),
      )
    }
  }
  return filteredComments
}

export function getFetchedQuickPlansComments(): Comment.QuickAccessComment[] {
  return usePlanStore().getPlansComments
}

/**
 * The comments subscription owns ongoing comment refreshes, but the very first
 * population must run after plans are loaded. This tells callers (the plans
 * subscription) whether that initial population has already happened.
 */
export function areQuickAccessCommentsInitialized(): boolean {
  const lastUpdateAt: Date | null = usePlanStore().getPlansCommentsLastUpdate
  return lastUpdateAt !== null
}

/**
 * This function will remove the element from the quickPlansComments array and return it.
 * Which means that is mutating the quickPlansComments array passed by parameters.
 */
function getElementFromQuickAcccessCommentsData(
  quickPlansComments: Comment.QuickAccessComment[],
  planId: string,
): Comment.QuickAccessComment | undefined {
  const index = quickPlansComments.findIndex(
    (comment) => comment.planId === planId,
  )
  if (index === -1) {
    return undefined
  } else {
    const element = quickPlansComments[index]
    quickPlansComments.splice(index, 1)
    return element
  }
}

function isPlanInLatestPlansToFetchComments(
  latestPlans: Plan.PlanForCommentFetching[],
  planId: string,
): boolean {
  return latestPlans.some((plan) => plan.planId === planId)
}

let quickAccessCommentsUpdate: Promise<void> | null = null
let quickAccessCommentsRerunRequested = false

/**
 * Public entry point for refreshing the quick-access plan comments.
 *
 * Several realtime subscriptions (plans + comments) can request a refresh at the
 * same time, especially on startup. Running them concurrently meant every caller
 * did the full chunked Firestore scan before any of them committed `lastUpdateAt`,
 * multiplying our read quota. This guard collapses concurrent calls into a single
 * in-flight run and, if a caller arrived while a run was ongoing, performs exactly
 * one trailing run so changes that landed mid-run are still picked up — cheaply,
 * because the trailing run now sees the committed `lastUpdateAt` and hits the gate.
 */
export async function updateQuickAcessPlansComments(): Promise<void> {
  if (quickAccessCommentsUpdate) {
    console.debug(
      '📃 ⏳ Comments update already in progress, queueing a single rerun',
    )
    quickAccessCommentsRerunRequested = true
    return quickAccessCommentsUpdate
  }

  quickAccessCommentsUpdate = (async () => {
    do {
      quickAccessCommentsRerunRequested = false
      await runQuickAccessPlansCommentsUpdate()
    } while (quickAccessCommentsRerunRequested)
  })().finally(() => {
    quickAccessCommentsUpdate = null
  })

  return quickAccessCommentsUpdate
}

async function runQuickAccessPlansCommentsUpdate(): Promise<void> {
  console.debug('📃 🤚 Updating quick access plans comments')
  const meta = getAppMetadata()
  const environment = BrowserEnvironmentResolver.getInstance()

  const lastUpdateAt: Date | null = usePlanStore().getPlansCommentsLastUpdate
  // const availablePlans: Plan.PlanForCommentFetching[] = [
  //   { planId: 'H0Ucn4d-RD75qryHo1OY8', planName: 'ttt', createdBy: 'inavarro+fi1@cartodb.com' },
  //   { planId: '4-_OE0w5ob4g1npzAfMZR', planName: 'vicente', createdBy: 'inavarro+fi1@cartodb.com' },
  // ]
  const availablePlans: Plan.PlanForCommentFetching[] =
    getFetchedPlansForComments()
  if (availablePlans.length === 0) {
    console.debug('📃 ✅ No plans available, early return')
    return
  }
  // const quickPlansComments: Comment.QuickAccessComment[] = [
  //   {
  //     planName: 'ttt',
  //     planId: 'H0Ucn4d-RD75qryHo1OY8',
  //     comments: 3,
  //     latestComment: new Date('2024-11-28T14:48:30.712Z'),
  //     created_by: 'inavarro+fi1@cartodb.com',
  //   },
  //   {
  //     planName: 'vicente',
  //     planId: '4-_OE0w5ob4g1npzAfMZR',
  //     comments: 2,
  //     latestComment: new Date('2024-11-28T14:48:30.712Z'),
  //     created_by: 'inavarro+fi1@cartodb.com',
  //   },
  // ]
  const quickPlansComments: Comment.QuickAccessComment[] =
    getFetchedQuickPlansComments()

  // Get the latest comments and check if they are in the available plans
  const latestPlanComments = await CommentsService.getLatestPlanComments(
    meta,
    environment,
  )

  // Lets see if we have to do an early return
  if (latestPlanComments.length === 0) {
    console.debug('📃 ✅ No comments found, early return')
    return
  }

  let latestPlansVisibleByUserForCommentFetching: Plan.PlanForCommentFetching[] =
    [...availablePlans]
  // On the very first run (lastUpdateAt === null) we populate counts for every
  // visible plan. On every run after that we only refetch plans whose latest
  // comment is newer than our last update, so a redundant trigger costs just the
  // cheap getLatestPlanComments read above instead of the full chunked scan.
  if (lastUpdateAt !== null) {
    // Only manage those updated plans visible by the user
    const latestPlanCommentsVisibleByUser = latestPlanComments.filter(
      (subscribedPlanComment) => {
        const isVisibleByUser = availablePlans.some(
          (plan) => plan.planId === subscribedPlanComment.planId,
        )
        return isVisibleByUser
      },
    )
    latestPlanCommentsVisibleByUser.sort(
      (a, b) => (b.created_at?.getTime() ?? 0) - (a.created_at?.getTime() ?? 0),
    )
    console.debug(
      '📃 Comments for the following plans need to be fetched: ',
      latestPlanCommentsVisibleByUser,
    )

    // After filtering only plans visible by the user, check if the latest comment is older than the last update
    if (latestPlanCommentsVisibleByUser.length === 0) {
      console.debug('📃 ✅ No plans need to be refeched')
      return
    }

    const mostRecentComment = latestPlanCommentsVisibleByUser[0].created_at
    if (!mostRecentComment) {
      console.error(
        '📃 Expected created_at value from latestComments, but no value found',
      )
      throw new Error(
        'Expected created_at value from latestComments, but no value found',
      )
    }

    if (lastUpdateAt && mostRecentComment && mostRecentComment < lastUpdateAt) {
      console.debug(
        `📃 ✅ Latest comment fetched is from ${mostRecentComment.toISOString()} and latest update was done at ${lastUpdateAt.toISOString()}`,
      )
      return
    }

    usePlanStore().setLatestPlansCommentsUpdated(
      latestPlanCommentsVisibleByUser.map((plan) => plan.planId),
    )

    latestPlansVisibleByUserForCommentFetching = availablePlans.filter(
      (plan) => {
        return latestPlanCommentsVisibleByUser.some(
          (latestPlan) => plan.planId === latestPlan.planId,
        )
      },
    )
  }

  // Get all the comments for the updated plans visible by the user
  const newQuickPlansComments = await CommentsService.getAllNewComments(
    meta,
    environment,
    latestPlansVisibleByUserForCommentFetching,
  )
  console.debug(
    '📃 New quick plans comments retreived: ',
    newQuickPlansComments,
  )

  // Merge updated plan comments with the existing ones
  const updatedQuickPlansComments: Comment.QuickAccessComment[] = []

  if (lastUpdateAt !== null) {
    quickPlansComments.forEach((planComment) => {
      const planId = planComment.planId
      if (
        !isPlanInLatestPlansToFetchComments(
          latestPlansVisibleByUserForCommentFetching,
          planId,
        )
      ) {
        updatedQuickPlansComments.push(planComment)
      } else {
        const updatedQuickPlanComments = getElementFromQuickAcccessCommentsData(
          newQuickPlansComments,
          planId,
        )
        if (updatedQuickPlanComments) {
          updatedQuickPlansComments.push(updatedQuickPlanComments)
        }
      }
    })
  }
  updatedQuickPlansComments.push(...newQuickPlansComments)

  // Sort the comments by the latest comment date decreasing
  updatedQuickPlansComments.sort(
    (a, b) => b.latestComment.getTime() - a.latestComment.getTime(),
  )

  usePlanStore().setPlansCommentsLastUpdate(new Date())
  console.debug(
    '📃 ✅ Quick comments updated with data: ',
    updatedQuickPlansComments,
  )
  usePlanStore().setPlansCommentsData(updatedQuickPlansComments)
}

export async function subscribeToComments(
  userId: string,
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
): Promise<void> {
  if (!isQuickAccessCommentEnabled(meta)) {
    return
  }
  const isSubscribed = usePlanStore().getIsSubscribedToComments
  if (isSubscribed) {
    console.warn(
      'Already subscribed to comments. This should be not happening, please check how this is being called',
    )
    console.debug('📃 ✋ Already subscribed to comments. Nothing to do')
    return
  }
  console.debug('📃📃📃📃 Subscribing to comments 📃📃📃📃')
  const debounceDelay = getRealtimeReactionForQuickAccessComments(meta)
  console.debug('📃 Debounce delay for comments in seconds: ', debounceDelay)
  const unscribeFunction = await subscribeToCommentsService(
    meta,
    environment,
    debounceDelay,
    updateQuickAcessPlansComments,
  )
  window.addEventListener('beforeunload', () => {
    if (unscribeFunction) {
      console.debug('📃 ✅ Unsubscribing from comments before closing')
      unscribeFunction()
      usePlanStore().setIsSubscribedToComments(false)
    }
  })
  usePlanStore().setIsSubscribedToComments(true)
}
